import { createContext, useContext, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  BarChart3, Users, Wallet, Banknote, CalendarDays, HeartPulse, Gift, ShoppingBag,
  Route, Fuel, Car, TrendingUp, Smartphone, FileSpreadsheet, Calculator, BookOpenCheck,
  Scale, Receipt, ArrowLeftRight, AlertTriangle, Search,
} from "lucide-react";
const QCtx = createContext("");

// Unified reports hub (التقارير): groups every report in the system by category and
// routes to the relevant panel/view. Operations & HR navigate within the dashboard;
// Accounts opens the AlFurat finance module reports.
const CARD = "group flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 text-start transition-all hover:border-blue-300 hover:shadow-sm w-full";

const Item = ({ icon: Icon, label, desc, tone, onClick, testid }) => {
  const q = useContext(QCtx).trim();
  if (q && !`${label} ${desc || ""}`.includes(q)) return null;
  return (
  <button type="button" onClick={onClick} data-testid={testid} className={CARD}>
    <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${tone}`}><Icon className="h-5 w-5" /></span>
    <span className="min-w-0">
      <span className="block font-semibold text-slate-800 text-sm truncate">{label}</span>
      {desc && <span className="block text-[11px] text-slate-400 truncate">{desc}</span>}
    </span>
  </button>
  );
};

const Group = ({ icon: Icon, title, accent, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-4" data-testid={`reports-group-${title}`}>
    <div className="flex items-center gap-2 mb-3">
      <span className={`grid h-9 w-9 place-items-center rounded-xl ${accent}`}><Icon className="h-5 w-5" /></span>
      <h3 className="font-heading font-bold text-slate-700">{title}</h3>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

export const ReportsHubPanel = ({ onNavigate, sector = "delivery", canEdit = false }) => {
  const navigate = useNavigate();
  const go = (v) => () => onNavigate?.(v);
  const isDelivery = sector === "delivery";
  const [q, setQ] = useState("");

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="reports-hub-panel" dir="rtl">
      <div className="flex items-center gap-2.5 mb-5">
        <div className="h-11 w-11 rounded-2xl grid place-items-center bg-blue-600 shadow-sm"><BarChart3 className="h-5 w-5 text-white" /></div>
        <div>
          <h2 className="font-heading font-bold text-xl text-slate-800">التقارير</h2>
          <p className="text-sm text-slate-400">كل تقارير النظام في مكان واحد — اختر التقرير لعرضه أو تصديره</p>
        </div>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث عن تقرير… (رواتب، وقود، مخالفات)" data-testid="reports-search"
          className="h-10 w-full rounded-xl border border-slate-200 bg-white pr-9 pl-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/40" />
      </div>
      <QCtx.Provider value={q}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* HR */}
        <Group icon={Users} title="تقارير الموارد البشرية" accent="bg-emerald-100 text-emerald-700">
          <Item icon={Banknote} tone="bg-emerald-50 text-emerald-600" label="كشف الرواتب" desc="رواتب الموظفين والبدلات والاستقطاعات" onClick={go("salaries")} testid="report-salaries" />
          <Item icon={Wallet} tone="bg-rose-50 text-rose-600" label="تقرير السُّلَف" desc="سلف الموظفين والمسدَّد والمتبقي" onClick={go("advances")} testid="report-advances" />
          <Item icon={Users} tone="bg-blue-50 text-blue-600" label="بيانات الموظفين والوثائق" desc="الإقامات والجوازات وتواريخ الانتهاء" onClick={go("employees")} testid="report-employees" />
          <Item icon={CalendarDays} tone="bg-amber-50 text-amber-600" label="الحضور والانصراف" desc="سجل الحضور وساعات العمل" onClick={go("attendance")} testid="report-attendance" />
          <Item icon={CalendarDays} tone="bg-sky-50 text-sky-600" label="الإجازات" desc="رصيد وطلبات الإجازات" onClick={go("leave")} testid="report-leave" />
          <Item icon={HeartPulse} tone="bg-rose-50 text-rose-600" label="إصابات العمل" desc="سجل الإصابات والحوادث" onClick={go("injuries")} testid="report-injuries" />
          <Item icon={Gift} tone="bg-fuchsia-50 text-fuchsia-600" label="الولاء والمكافآت" desc="نقاط الولاء والمكافآت" onClick={go("loyalty")} testid="report-loyalty" />
        </Group>

        {/* Operations */}
        <Group icon={Route} title="تقارير التشغيل" accent="bg-indigo-100 text-indigo-700">
          {isDelivery ? (
            <>
              <Item icon={TrendingUp} tone="bg-indigo-50 text-indigo-600" label="الأداء العام للمندوبين" desc="إجمالي الطلبات والساعات والتقييم" onClick={go("riders")} testid="report-riders" />
              <Item icon={CalendarDays} tone="bg-blue-50 text-blue-600" label="الأداء اليومي / تقرير الطلبات" desc="طلبات كل يوم لكل مندوب" onClick={go("riders-daily")} testid="report-riders-daily" />
              <Item icon={BarChart3} tone="bg-violet-50 text-violet-600" label="التحليل الذكي ومقارنة التطبيقات" desc="مقارنة أداء المنصات عبر فترة" onClick={go("riders-compare")} testid="report-riders-compare" />
              <Item icon={TrendingUp} tone="bg-amber-50 text-amber-600" label="تحقيق التارقت" desc="إنجاز المندوبين للأهداف" onClick={go("riders-target")} testid="report-riders-target" />
              <Item icon={Route} tone="bg-emerald-50 text-emerald-600" label="مسافات المندوبين" desc="الكيلومترات المقطوعة والمستحقة" onClick={go("riders-distances")} testid="report-riders-distances" />
              <Item icon={Fuel} tone="bg-orange-50 text-orange-600" label="تكلفة الوقود" desc="استهلاك وتكلفة الوقود" onClick={go("fuel")} testid="report-fuel" />
              <Item icon={AlertTriangle} tone="bg-rose-50 text-rose-600" label="المخالفات المرورية" desc="سجل المخالفات والغرامات" onClick={go("violations")} testid="report-violations" />
              <Item icon={Smartphone} tone="bg-sky-50 text-sky-600" label="استخدام تطبيق المندوب" desc="تفعيل واستخدام البوابة" onClick={go("portal-usage")} testid="report-portal-usage" />
              <Item icon={ShoppingBag} tone="bg-teal-50 text-teal-600" label="مشتريات المندوب" desc="مشتريات قطع الغيار والصيانة" onClick={go("purchases")} testid="report-purchases" />
            </>
          ) : (
            <Item icon={BarChart3} tone="bg-indigo-50 text-indigo-600" label="تقرير المبيعات" desc="ملخص المبيعات" onClick={go("sales")} testid="report-sales" />
          )}
          <Item icon={Car} tone="bg-slate-100 text-slate-600" label="المركبات والعُهد" desc="المركبات وسجل التسليم والفحص" onClick={go("vehicles")} testid="report-vehicles" />
        </Group>

        {/* Accounts (AlFurat finance module) */}
        <Group icon={Calculator} title="تقارير الحسابات" accent="bg-amber-100 text-amber-700">
          <Item icon={BookOpenCheck} tone="bg-amber-50 text-amber-600" label="القيود اليومية" desc="دفتر اليومية" onClick={() => navigate("/finance/journals")} testid="report-fin-journals" />
          <Item icon={Scale} tone="bg-blue-50 text-blue-600" label="ميزان المراجعة" desc="أرصدة الحسابات" onClick={() => navigate("/finance/trial-balance")} testid="report-fin-trial" />
          <Item icon={Receipt} tone="bg-emerald-50 text-emerald-600" label="كشف حساب لكل الحسابات" desc="حركة الحسابات التفصيلية" onClick={() => navigate("/finance/accounts")} testid="report-fin-accounts" />
          <Item icon={ArrowLeftRight} tone="bg-violet-50 text-violet-600" label="برنامج WorkZone المحاسبي" desc="فتح نظام الحسابات الكامل والتقارير المالية" onClick={() => navigate("/finance")} testid="report-fin-home" />
          {isDelivery && <Item icon={FileSpreadsheet} tone="bg-teal-50 text-teal-600" label="الإيراد" desc="إيرادات المنصات" onClick={go("revenues")} testid="report-revenues" />}
        </Group>
      </div>
      </QCtx.Provider>
    </motion.div>
  );
};
